<template>
  <v-dialog v-model="internalValue" max-width="850px" persistent scrollable>
    <v-card class="rounded-xl border-light" outlined elevation="0">
      <v-card-title class="py-3 px-5 d-flex align-center justify-space-between border-light-bottom bg-white">
        <div class="d-flex align-center">
          <v-icon color="primary" class="mr-2">{{ isEditing ? 'mdi-pencil-outline' : 'mdi-clock-plus-outline' }}</v-icon>
          <span class="text-subtitle-1 font-weight-black grey--text text--darken-4 text-uppercase">
            {{ isEditing ? 'Editar Sesión de Capacitación' : 'Nueva Sesión de Capacitación' }}
          </span>
        </div>
        <v-btn icon small @click="close">
          <v-icon color="grey darken-2">mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="py-5 px-6">
        <v-row dense>
          <!-- Evento Asignado -->
          <v-col cols="12" md="8">
            <s-select
              label="Evento de Capacitación *"
              :items="eventItems"
              v-model="form.TevID"
              :rules="[v => !!v || 'El evento es obligatorio']"
            />
          </v-col>
          <v-col cols="12" md="4">
            <s-text v-model.number="form.TseSessionNumber" label="Número de Sesión *" number />
          </v-col>

          <!-- Tema Específico & Capacitador Asignado -->
          <v-col cols="12" md="6">
            <s-select
              label="Tema a Dictar"
              :items="topicItems"
              v-model="form.TtpID"
              clearable
              @change="onTopicChange"
            />
          </v-col>
          <v-col cols="12" md="6">
            <s-select
              label="Capacitador Asignado"
              :items="trainerItems"
              v-model="form.TrnID"
              clearable
            />
          </v-col>

          <!-- Título o Detalle de la Sesión -->
          <v-col cols="12">
            <s-text v-model="form.TseTopicTitle" label="Título / Módulo de la Sesión" placeholder="Ej: Módulo 1: Introducción a la Ergonomía" />
          </v-col>

          <!-- Fecha y Horarios -->
          <v-col cols="12" sm="4">
            <s-date v-model="form.TseDate" label="Fecha de la Sesión *" />
          </v-col>
          <v-col cols="6" sm="4">
            <s-text type="time" v-model="form.TseStartTime" label="Hora Inicio *" @input="recalculateDuration" />
          </v-col>
          <v-col cols="6" sm="4">
            <s-text type="time" v-model="form.TseEndTime" label="Hora Fin *" @input="recalculateDuration" />
          </v-col>

          <!-- Duración Calculada y Modalidad -->
          <v-col cols="12" sm="4">
            <s-text v-model.number="form.TseDurationMinutes" label="Duración (minutos)" number readonly />
          </v-col>
          <v-col cols="12" sm="4">
            <s-select-definition :def="1459" v-model="form.TypeModality" label="Modalidad" clearable />
          </v-col>
          <v-col cols="12" sm="4">
            <s-select
              label="Estado de la Sesión"
              :items="statusItems"
              v-model="form.TseStatus"
            />
          </v-col>

          <!-- Sitio o Enlace Virtual Dinámico según Modalidad -->
          <v-col cols="12" v-if="form.TypeModality === 1 || form.TypeModality === 3">
            <s-select-definition :def="1560" v-model="form.TseSiteOrLink" label="Lugar / Sala de Capacitación" clearable />
          </v-col>
          <v-col cols="12" v-else-if="form.TypeModality === 2">
            <s-text v-model="form.TseSiteOrLink" label="Enlace Virtual (Teams / Zoom / Meet)" placeholder="https://teams.microsoft.com/..." />
          </v-col>
          <v-col cols="12" v-else>
            <s-text v-model="form.TseSiteOrLink" label="Lugar o Enlace de Conexión" placeholder="Sala o enlace de reunión" />
          </v-col>

          <!-- Enlace de Grabación de la Sesión -->
          <v-col cols="12" sm="6">
            <s-text
              v-model="form.TseRecordingLink"
              label="Enlace de Grabación (Teams / Stream / Drive)"
              placeholder="https://teams.microsoft.com/... o enlace de Drive"
            />
          </v-col>

          <!-- Enlace Web de la Presentación / PPT -->
          <v-col cols="12" sm="6">
            <s-text
              v-model="form.TseMaterialLink"
              label="Enlace de Presentación / PPT (SharePoint / Canva / Drive)"
              placeholder="https://sunshine.sharepoint.com/... o enlace web"
            />
          </v-col>

          <!-- Archivo de Presentación (PPTX / PPT / PDF) -->
          <v-col cols="12">
            <div class="d-flex align-center">
              <v-file-input
                v-model="materialFile"
                label="Subir Diapositivas / Presentación (PPTX, PPT, PDF)"
                accept=".ppt,.pptx,.pdf,.pps,.ppsx"
                prepend-icon=""
                prepend-inner-icon="mdi-file-powerpoint"
                outlined
                dense
                hide-details
                class="rounded-lg mr-2"
                :placeholder="form.TseMaterialPath ? getFileName(form.TseMaterialPath) : 'Seleccione archivo PPT o PDF'"
              ></v-file-input>
              <v-btn
                v-if="form.TseMaterialPath"
                icon
                small
                color="orange darken-2"
                @click="downloadCurrentMaterial"
                title="Descargar presentación actual"
              >
                <v-icon small>mdi-download</v-icon>
              </v-btn>
              <v-btn
                v-if="form.TseMaterialPath"
                icon
                small
                color="error"
                @click="removeCurrentMaterial"
                title="Quitar archivo actual"
              >
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="py-3 px-6 justify-end bg-white">
        <v-btn text class="rounded-lg text-none font-weight-bold grey--text text--darken-3 px-4" @click="close">
          Cancelar
        </v-btn>
        <v-btn color="primary" class="rounded-lg text-none font-weight-bold px-6 white--text" elevation="0" :loading="saving" @click="save">
          <v-icon left small>mdi-content-save</v-icon>
          Guardar Sesión
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import SText from "@/components/Utils/SText.vue";
import SDate from "@/components/Utils/SDate.vue";
import SSelect from "@/components/Utils/SSelect.vue";
import SSelectDefinition from "@/components/Utils/SSelectDefinition.vue";
import _GenUploadFiles from "@/services/General/GenUploadFiles";
import TrainingSessionService from "@/services/Management/TrainingSessionService";
import TrainingEventService from "@/services/Management/TrainingEventService";
import TrainingTopicService from "@/services/Management/TrainingTopicService";
import TrainerService from "@/services/Management/TrainerService";

export default {
  name: "TrainingSessionFormModal",
  components: {
    SText,
    SDate,
    SSelect,
    SSelectDefinition,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    editItem: {
      type: Object,
      default: null,
    },
    defaultTevID: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      saving: false,
      eventItems: [],
      topicItems: [],
      trainerItems: [],
      statusItems: [
        { text: "Programada", value: 1 },
        { text: "En curso", value: 2 },
        { text: "Realizada", value: 3 },
        { text: "Cancelada", value: 4 },
      ],
      materialFile: null,
      form: {
        TseID: 0,
        TevID: null,
        TtpID: null,
        TrnID: null,
        TseSessionNumber: 1,
        TseTopicTitle: "",
        TseDate: new Date().toISOString().substr(0, 10),
        TseStartTime: "08:00",
        TseEndTime: "10:00",
        TseDurationMinutes: 120,
        TypeModality: null,
        TseSiteOrLink: "",
        TseRecordingLink: "",
        TseSignedAttendancePath: null,
        TseMaterialLink: "",
        TseMaterialPath: null,
        TseStatus: 1,
        SecStatus: 1,
      },
    };
  },
  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    isEditing() {
      return this.form.TseID > 0;
    },
  },
  watch: {
    value(val) {
      if (val) {
        this.materialFile = null;
        this.loadCatalogs();
        if (this.editItem && this.editItem.TseID) {
          this.form = Object.assign({}, this.editItem);
          if (this.form.TseDate && this.form.TseDate.includes("T")) {
            this.form.TseDate = this.form.TseDate.split("T")[0];
          }
        } else {
          this.resetForm();
          if (this.defaultTevID) {
            this.form.TevID = this.defaultTevID;
          }
        }
      }
    },
  },
  methods: {
    getFileName(path) {
      if (!path) return "";
      return path.split("/").pop();
    },
    async downloadCurrentMaterial() {
      if (!this.form.TseMaterialPath) return;
      try {
        await _GenUploadFiles.getFile("PathTrainingMaterials", this.form.TseMaterialPath);
      } catch (err) {
        this.$fun.alert("Error al descargar el archivo de presentación.", "error");
      }
    },
    removeCurrentMaterial() {
      this.$fun.alert("¿Está seguro de quitar el archivo de presentación actual?", "question").then((r) => {
        if (r && r.value) {
          this.form.TseMaterialPath = null;
        }
      });
    },
    loadCatalogs() {
      const reqId = this.$fun.getUserID();
      TrainingEventService.list({ SecStatus: 1 }, reqId).then((res) => {
        this.eventItems = (res.data || []).map((e) => ({ text: e.TevName, value: e.TevID }));
      });
      TrainingTopicService.list({ SecStatus: 1 }, reqId).then((res) => {
        this.topicItems = (res.data || []).map((t) => ({ text: t.TtpTitle, value: t.TtpID, duration: t.TtpDurationMinutes }));
      });
      TrainerService.list({ SecStatus: 1 }, reqId).then((res) => {
        this.trainerItems = (res.data || []).map((t) => ({ text: `${t.TrnFullName} (${t.TrnOrganization || 'Interno'})`, value: t.TrnID }));
      });
    },
    onTopicChange(ttpID) {
      const selectedTopic = this.topicItems.find((t) => t.value === ttpID);
      if (selectedTopic) {
        if (!this.form.TseTopicTitle) {
          this.form.TseTopicTitle = selectedTopic.text;
        }
        if (selectedTopic.duration) {
          this.form.TseDurationMinutes = selectedTopic.duration;
        }
      }
    },
    recalculateDuration() {
      if (this.form.TseStartTime && this.form.TseEndTime) {
        const start = this.form.TseStartTime.split(":").map(Number);
        const end = this.form.TseEndTime.split(":").map(Number);
        const diff = (end[0] * 60 + end[1]) - (start[0] * 60 + start[1]);
        this.form.TseDurationMinutes = diff > 0 ? diff : 0;
      }
    },
    resetForm() {
      this.materialFile = null;
      this.form = {
        TseID: 0,
        TevID: this.defaultTevID || null,
        TtpID: null,
        TrnID: null,
        TseSessionNumber: 1,
        TseTopicTitle: "",
        TseDate: new Date().toISOString().substr(0, 10),
        TseStartTime: "08:00",
        TseEndTime: "10:00",
        TseDurationMinutes: 120,
        TypeModality: null,
        TseSiteOrLink: "",
        TseRecordingLink: "",
        TseSignedAttendancePath: null,
        TseMaterialLink: "",
        TseMaterialPath: null,
        TseStatus: 1,
        SecStatus: 1,
      };
    },
    close() {
      this.internalValue = false;
    },
    async save() {
      if (!this.form.TevID) {
        this.$fun.alert("Debe seleccionar un evento de capacitación.", "warning");
        return;
      }
      if (!this.form.TseDate) {
        this.$fun.alert("Seleccione la fecha de la sesión.", "warning");
        return;
      }
      if (!this.form.TseStartTime || !this.form.TseEndTime) {
        this.$fun.alert("Ingrese la hora de inicio y fin de la sesión.", "warning");
        return;
      }

      const confirmed = await this.$fun.alert("¿Está seguro de guardar la sesión de capacitación?", "question");
      if (!confirmed || !confirmed.value) return;

      this.saving = true;
      try {
        if (this.materialFile && typeof this.materialFile === "object" && this.materialFile instanceof File) {
          const sessionId = this.form.TseID || "SES";
          const uniqueName = `Material_Ses_${sessionId}_${Date.now()}`;
          const res = await _GenUploadFiles.upload(this.materialFile, uniqueName, "PathTrainingMaterials", true);
          this.form.TseMaterialPath = res?.data?.fileName || `${uniqueName}.${this.materialFile.name.split('.').pop()}`;
        }

        const reqId = this.$fun.getUserID();
        await TrainingSessionService.save(this.form, reqId);
        this.$fun.alert("Sesión guardada correctamente.", "success");
        this.$emit("saved");
        this.close();
      } catch (err) {
        this.$fun.alert(err?.response?.data?.Message || "Error al guardar la sesión.", "error");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
